import { useState, useEffect } from 'react';
import { storage } from '../utils/storage';
import { doc, getDoc } from 'firebase/firestore';
import { db, auth } from '../config/firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { presenceService } from '../services/presenceService';

export interface AuthState {
  isLoading: boolean;
  isAuthenticated: boolean;
  userId: string | null;
  userType: string | null;
  userData: any | null;
}

export const useAuth = () => {
  const [authState, setAuthState] = useState<AuthState>({
    isLoading: true,
    isAuthenticated: false,
    userId: null,
    userType: null,
    userData: null,
  });

  useEffect(() => {
    // Verificar autenticação do Firebase Auth primeiro
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        // Usuário autenticado no Firebase Auth
        console.log('🔐 [AUTH] Usuário autenticado no Firebase Auth:', firebaseUser.uid);
        // Verificar dados no Firestore
        await checkAuthState();
      } else {
        // Usuário não autenticado no Firebase Auth
        console.log('🔐 [AUTH] Usuário não autenticado no Firebase Auth');
        setAuthState({
          isLoading: false,
          isAuthenticated: false,
          userId: null,
          userType: null,
          userData: null,
        });
      }
    });

    return () => unsubscribe();
  }, []);

  const checkAuthState = async () => {
    const minLoadingTime = 1000; // 1 segundo mínimo para web
    const startTime = Date.now();
    
    try {
      console.log('🔐 [AUTH] Verificando estado de autenticação...');
      
      // Priorizar usuário do Firebase Auth
      const firebaseUser = auth.currentUser;
      let userId: string | null = null;
      
      if (firebaseUser) {
        userId = firebaseUser.uid;
        console.log('🔐 [AUTH] Usando usuário do Firebase Auth:', userId);
      } else {
        // Fallback para localStorage
        userId = await storage.getItem('userId');
        console.log('🔐 [AUTH] Usando userId do localStorage:', userId);
      }
      
      const userType = await storage.getItem('userType');
      const authToken = await storage.getItem('authToken');

      console.log('🔐 [AUTH] Dados do localStorage:', { 
        userId: !!userId, 
        userType, 
        hasToken: !!authToken,
        hasFirebaseUser: !!firebaseUser
      });

      if (!userId || !userType) {
        console.log('🔐 [AUTH] Usuário não autenticado - dados incompletos');
        
        const elapsedTime = Date.now() - startTime;
        const remainingTime = Math.max(0, minLoadingTime - elapsedTime);
        if (remainingTime > 0) {
          await new Promise(resolve => setTimeout(resolve, remainingTime));
        }
        
        setAuthState({
          isLoading: false,
          isAuthenticated: false,
          userId: null,
          userType: null,
          userData: null,
        });
        return;
      }

      // Verificar se o usuário ainda existe no Firestore
      const userDoc = await getDoc(doc(db, 'usuarios', userId));
      
      if (!userDoc.exists()) {
        console.log('🔐 [AUTH] Usuário não encontrado no Firestore - limpando dados');
        await clearAuthData();
        
        const elapsedTime = Date.now() - startTime;
        const remainingTime = Math.max(0, minLoadingTime - elapsedTime);
        if (remainingTime > 0) {
          await new Promise(resolve => setTimeout(resolve, remainingTime));
        }
        
        setAuthState({
          isLoading: false,
          isAuthenticated: false,
          userId: null,
          userType: null,
          userData: null,
        });
        return;
      }

      const userData = { id: userId, ...userDoc.data() } as any;
      
      // Verificar se o tipo de usuário ainda é válido
      if (userData.tipo !== userType) {
        console.log('🔐 [AUTH] Tipo de usuário alterado - atualizando');
        await storage.setItem('userType', userData.tipo);
      }

      // Se for uma empresa, buscar dados adicionais da empresa
      if (userData.tipo === 'Empresa' && userData.empresaId) {
        try {
          const empresaDoc = await getDoc(doc(db, 'Empresa', userData.empresaId));
          if (empresaDoc.exists()) {
            const empresaData = empresaDoc.data();
            userData.nome = empresaData.nomeEmpresa || userData.nome || 'Empresa';
            userData.foto = empresaData.foto || empresaData.fotoUrl || userData.foto;
          }
        } catch (error) {
          console.error('🔐 [AUTH] Erro ao carregar dados da empresa:', error);
        }
      }

      console.log('🔐 [AUTH] Usuário autenticado com sucesso:', {
        userId,
        userType: userData.tipo,
        nome: userData.nome
      });

      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, minLoadingTime - elapsedTime);
      if (remainingTime > 0) {
        await new Promise(resolve => setTimeout(resolve, remainingTime));
      }

      setAuthState({
        isLoading: false,
        isAuthenticated: true,
        userId,
        userType: userData.tipo,
        userData,
      });

      // Iniciar rastreamento de presença
      try {
        await presenceService.startPresence(userId, {
          userType: userData.tipo,
          nome: userData.nome || 'Usuário',
          empresaId: userData.empresaId
        });
      } catch (error) {
        console.error('Erro ao iniciar presença:', error);
      }

    } catch (error) {
      console.error('🔐 [AUTH] Erro ao verificar autenticação:', error);
      
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, minLoadingTime - elapsedTime);
      if (remainingTime > 0) {
        await new Promise(resolve => setTimeout(resolve, remainingTime));
      }
      
      setAuthState({
        isLoading: false,
        isAuthenticated: false,
        userId: null,
        userType: null,
        userData: null,
      });
    }
  };

  const clearAuthData = async () => {
    try {
      await Promise.all([
        storage.removeItem('userId'),
        storage.removeItem('userType'),
        storage.removeItem('authToken'),
        storage.removeItem('empresaId'),
        storage.removeItem('emailSalvo'),
        storage.removeItem('senhaSalva'),
        storage.removeItem('lembrarDados'),
      ]);
      console.log('🔐 [AUTH] Dados de autenticação limpos');
    } catch (error) {
      console.error('🔐 [AUTH] Erro ao limpar dados:', error);
    }
  };

  const logout = async () => {
    try {
      const userId = authState.userId;
      
      // Parar rastreamento de presença antes de fazer logout
      if (userId) {
        try {
          await presenceService.stopPresence(userId);
        } catch (error) {
          console.error('Erro ao parar presença:', error);
        }
      }
      
      // Fazer logout do Firebase Auth
      if (auth.currentUser) {
        await signOut(auth);
        console.log('🔐 [AUTH] Logout do Firebase Auth realizado');
      }
    } catch (error) {
      console.error('🔐 [AUTH] Erro ao fazer logout do Firebase Auth:', error);
    }
    
    await clearAuthData();
    setAuthState({
      isLoading: false,
      isAuthenticated: false,
      userId: null,
      userType: null,
      userData: null,
    });
  };

  const getInitialRoute = (): string => {
    if (!authState.isAuthenticated || !authState.userType) {
      return '/login';
    }

    switch (authState.userType) {
      case 'Motoboy':
        return '/motoboy';
      case 'Empresa':
        return '/empresa';
      case 'Central':
        return '/central';
      default:
        return '/login';
    }
  };

  return {
    ...authState,
    logout,
    getInitialRoute,
    checkAuthState,
  };
};

